import { create } from "zustand";
import { persist } from "zustand/middleware";
import { supabase } from "@/integrations/supabase/client";

interface Workspace {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  role: "owner" | "admin" | "member" | "viewer" | "client";
}

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  fetchWorkspaces: () => Promise<void>;
  setCurrentWorkspace: (workspace: Workspace) => void;
  createWorkspace: (name: string, slug: string) => Promise<Workspace>;
  updateWorkspace: (id: string, updates: Partial<Workspace> & { logo_url?: string }) => Promise<void>;
}

export const useWorkspace = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      currentWorkspace: null,
      isLoading: false,

      fetchWorkspaces: async () => {
        set({ isLoading: true });
        try {
          const { data: { user } } = await supabase.auth.getUser();
          console.log("fetchWorkspaces: user", user?.id);
          if (!user) return;

          const { data, error } = await supabase
            .from("workspace_members")
            .select(`
              role,
              workspace:workspaces (
                id,
                name,
                slug,
                logo_url
              )
            `)
            .eq("user_id", user.id);

          console.log("fetchWorkspaces: query result", { data, error });

          if (error) throw error;

          const workspaces = data.map((item: any) => {
            if (!item.workspace) {
              console.warn("Workspace data missing for member record:", item);
              return null;
            }
            return {
              id: item.workspace.id,
              name: item.workspace.name,
              slug: item.workspace.slug,
              logo_url: item.workspace.logo_url,
              role: item.role,
            };
          }).filter(Boolean) as Workspace[];

          console.log("fetchWorkspaces: mapped workspaces", workspaces);

          set({ workspaces });

          // If current workspace is deleted or not in list anymore, select the first one
          const current = get().currentWorkspace;
          if (!current || !workspaces.find(w => w.id === current.id)) {
            set({ currentWorkspace: workspaces.length > 0 ? workspaces[0] : null });
          } else {
            // Update current workspace with fresh data (e.g. logo change)
            const updatedCurrent = workspaces.find(w => w.id === current.id);
            if (updatedCurrent) {
              set({ currentWorkspace: updatedCurrent });
            }
          }
        } catch (error) {
          console.error("Error fetching workspaces:", error);
        } finally {
          set({ isLoading: false });
        }
      },

      setCurrentWorkspace: (workspace) => {
        set({ currentWorkspace: workspace });
      },

      updateWorkspace: async (id, updates) => {
        set({ isLoading: true });
        try {
          const { error } = await supabase
            .from("workspaces")
            .update(updates)
            .eq("id", id);

          if (error) throw error;
          
          await get().fetchWorkspaces();
        } catch (error) {
          console.error("Error updating workspace:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      createWorkspace: async (name: string, slug: string) => {
        set({ isLoading: true });
        try {
          // Use the RPC function for atomic creation
          console.log("Calling create_workspace RPC with:", { name, slug });
          const { data: workspaceId, error } = await supabase
            .rpc("create_workspace", {
              name,
              slug,
            });

          if (error) {
            console.error("RPC create_workspace failed:", error);
            throw error;
          }

          console.log("Workspace created successfully via RPC. ID:", workspaceId);

          // Fetch the full workspace object to update the state
          const { data: workspace, error: fetchError } = await supabase
            .from("workspaces")
            .select("*")
            .eq("id", workspaceId)
            .single();

          if (fetchError) throw fetchError;
          
          // Refresh list
          await get().fetchWorkspaces();
          
          // Set as current
          const newWorkspace = {
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            role: "owner" as const,
          };
          
          set({ 
            workspaces: [...get().workspaces, newWorkspace],
            currentWorkspace: newWorkspace 
          });

          return newWorkspace;

        } catch (error) {
          console.error("Error creating workspace:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "arqum-workspace-storage",
    }
  )
);
